import { Component, OnInit, Inject, Optional, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { EventEmitterService } from '@core/services/event-emitter.service';
import { GameProviderHttpService } from '@core/services/game-provider-http.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'kt-promotion',
  templateUrl: './promotion-currency-setting.component.html',
  styleUrls: ['./promotion-currency-setting.component.scss']
})
export class PromotionCurrencySettingComponent implements OnInit, OnDestroy {
  form: FormGroup;
  dropdown = {
    currencies: [] as any[]
  };

  activeCurrencyId: number = 0;
  activeCurrency: string = '';
  currencyForms: Map<number, FormGroup> = new Map();
  currencyApiData: Map<number, any> = new Map();

  // Free-round bet values (AIO-71840, PTI; JILI2 added) — mirrors v1: for these providers, Amount
  // Per Line is chosen from a provider-supplied list of bet values (per game + currency), not free
  // input. JILI2's list comes from its GetGameBetList, one list per game and currency.
  static readonly BET_VALUE_PROVIDERS = ['PTI', 'JILI2'];
  hasBetValues = false;
  freeSpinProviderCode = '';
  betValuesByCurrency: Map<number, any[]> = new Map();
  // Loading / error are per-currency so a slow or empty load on one currency can't show a stale
  // spinner or the wrong currency's message under another tab.
  betValuesLoadingByCurrency: Map<number, boolean> = new Map();
  betValuesErrorByCurrency: Map<number, string> = new Map();

  // Active-tab views of the per-currency bet-value state (read by the template).
  get betValuesLoading(): boolean {
    return this.betValuesLoadingByCurrency.get(this.activeCurrencyId) || false;
  }
  get betValuesError(): string {
    return this.betValuesErrorByCurrency.get(this.activeCurrencyId) || '';
  }

  private formSubscriptions: Subscription[] = [];
  private saveSubscription: Subscription;

  constructor(
    private eventEmitterService: EventEmitterService,
    private gameProviderHttpService: GameProviderHttpService,
    @Optional() @Inject('initialData') private initialData: any,
    @Optional() @Inject('isEditMode') public isEditMode: boolean
  ) {}

  ngOnInit() {
    // Free Spin supports all currencies; PP / BTG are limited to MYR / AUD.
    const allCurrencies = JSON.parse(sessionStorage.getItem('currencies')) || [];
    this.dropdown.currencies = this.initialData?.promotion_type == 'Free Spin'
      ? allCurrencies
      : allCurrencies.filter((c: any) => ['MYR', 'AUD'].includes(c.name));

    this.formInit();
    if (this.dropdown.currencies.length > 0) {
      this.selectCurrency(this.dropdown.currencies[0].id);
    }
    this.currencyForms.forEach((form) => {
      const sub = form.valueChanges.subscribe(() => {
        this.eventEmitterService.onPromotionCurrencySettingData(this.getAllFormData());
      });
      this.formSubscriptions.push(sub);
    });
    this.eventEmitterService.onPromotionCurrencySettingData(this.getAllFormData());

    if (this.initialData?.promotion_type == 'Free Spin') {
      this.resolveProviderAndLoadBetValues();
    }
  }

  // Resolve the provider code from its id, then (if it is a bet-value provider) fetch the allowed
  // bet values for each currency. Mirrors v1's promotion-currency-details bet-values behavior.
  private resolveProviderAndLoadBetValues() {
    const providerId = this.initialData?.free_spin_game_provider_id;
    if (!providerId) { return; }

    this.gameProviderHttpService.getFreeSpinGameProvider().subscribe((providers: any[]) => {
      const provider = (providers || []).find((p: any) => p.id == providerId);
      this.freeSpinProviderCode = (provider?.code || '').toUpperCase();
      this.hasBetValues = PromotionCurrencySettingComponent.BET_VALUE_PROVIDERS.includes(this.freeSpinProviderCode);
      if (!this.hasBetValues) { return; }
      // A not-yet-configured currency has no provider-issued bet value. Clear its default 0.01
      // (not a valid bet value) so the Amount Per Line select shows "Please Select" — the operator
      // must pick a real value before the currency can be submitted/granted, instead of silently sending 0.01.
      this.dropdown.currencies.forEach((currency: any) => {
        if (!this.isCurrencyConfigured(currency.id)) {
          this.getCurrencyForm(currency.id).get('amount_per_line')?.setValue(null);
        }
      });
      this.dropdown.currencies.forEach((currency: any) => this.loadBetValuesForCurrency(currency));
    });
  }

  private loadBetValuesForCurrency(currency: any) {
    const gameCode = this.initialData?.free_spin_game_code;
    if (!gameCode) { return; }

    this.betValuesLoadingByCurrency.set(currency.id, true);
    this.betValuesErrorByCurrency.set(currency.id, '');
    this.gameProviderHttpService.getFreeRoundBetValues({
      game_provider_code: this.freeSpinProviderCode,
      game_code: [gameCode],
      currency: currency.name,
    }).subscribe({
      next: (values: any[]) => {
        const normalized = this.normalizeBetValues(values);
        this.betValuesByCurrency.set(currency.id, normalized);
        this.betValuesLoadingByCurrency.set(currency.id, false);
        this.resetAmountPerLineIfUnlisted(currency.id, normalized);
        // A successful-but-empty result means the game offers no bet values for this currency. Surface it
        // only for a not-yet-configured currency — that's the one whose amount_per_line was nulled and whose
        // Submit is blocked with no visible reason. A configured currency keeps its valid saved value in a
        // disabled select, so the message would be a false alarm instructing an impossible action.
        if (normalized.length === 0 && !this.isCurrencyConfigured(currency.id)) {
          this.betValuesErrorByCurrency.set(
            currency.id,
            `No Amount Per Line values available for ${currency.name} — turn this currency off or pick another game.`
          );
        }
      },
      error: (error: any) => {
        this.betValuesByCurrency.set(currency.id, []);
        this.betValuesLoadingByCurrency.set(currency.id, false);
        this.resetAmountPerLineIfUnlisted(currency.id, []);
        const msg = error?.error?.message ?? error?.messages ?? error?.message;
        this.betValuesErrorByCurrency.set(
          currency.id,
          Array.isArray(msg) ? msg.join(' ')
            : (msg || `Unable to load Amount Per Line values for ${currency.name}. Please try again.`)
        );
      },
    });
  }

  // A <select> whose value matches none of its options renders blank, not as the placeholder. The
  // default 0.01 is never a provider bet value, so clear it once the list is in and nothing matches —
  // then "Please Select" shows. A configured currency keeps its saved value in its disabled select.
  private resetAmountPerLineIfUnlisted(currencyId: number, betValues: any[]) {
    if (this.isCurrencyConfigured(currencyId)) { return; }
    const control = this.getCurrencyForm(currencyId).get('amount_per_line');
    if (!control || control.value === null) { return; }
    if (!betValues.some((bv: any) => bv.value == control.value)) {
      control.setValue(null);
    }
  }

  private normalizeBetValues(values: any[]): any[] {
    const rows = Array.isArray(values) ? values : [];
    return rows
      .map((item: any) => {
        const value = (item !== null && typeof item === 'object')
          ? (item.amount_per_line ?? item.bet_value ?? item.betValue ?? item.value ?? item.amount ?? item.bet)
          : item;
        return (value === null || value === undefined || value === '') ? null : { value, label: value };
      })
      .filter((x: any) => !!x);
  }

  getBetValuesForActive(): any[] {
    return this.betValuesByCurrency.get(this.activeCurrencyId) || [];
  }

  onChangeStatus(event: any) {
    this.getCurrencyForm(this.activeCurrencyId).patchValue({ status: event.target.checked ? 1 : 0 });
  }

  ngOnDestroy() {
    this.formSubscriptions.forEach(s => s.unsubscribe());
    if (this.saveSubscription) this.saveSubscription.unsubscribe();
  }

  private formInit() {
    this.dropdown.currencies.forEach(currency => {
      const existingData = this.initialData?.promotion_currency?.find(
        (x: any) => x.currency_id === currency.id
      );
      if (existingData) {
        this.currencyApiData.set(currency.id, existingData);
      }
      
      let excludePromoTypeList = ['PP Promotion'];
      let phase1_starting_amount = null;
      let phase1_target_type = null;
      let phase1_target_amount = null;
      let phase2_starting_amount = null;

      if (!this.isEditMode) {
        phase1_starting_amount = (currency.name == 'MYR' || currency.name == 'AUD') && !excludePromoTypeList.includes(this.initialData?.promotion_type) ? 100 : existingData?.phase1_starting_amount ?? null;
        phase1_target_type = (currency.name == 'MYR' || currency.name == 'AUD') && !excludePromoTypeList.includes(this.initialData?.promotion_type) ? 7 : existingData?.phase1_target_type ?? null;
        phase1_target_amount = (currency.name == 'MYR' || currency.name == 'AUD') && !excludePromoTypeList.includes(this.initialData?.promotion_type) ? 3000 : existingData?.phase1_target_amount ?? null;
        phase2_starting_amount = (currency.name == 'MYR' || currency.name == 'AUD') && !excludePromoTypeList.includes(this.initialData?.promotion_type) ? 10 : existingData?.phase2_starting_amount ?? null;
      }
      else {
        phase1_starting_amount = existingData?.phase1_starting_amount;
        phase1_target_type = existingData?.phase1_target_type;
        phase1_target_amount = existingData?.phase1_target_amount;
        phase2_starting_amount = existingData?.phase2_starting_amount;
      }

      const toFixed = (val: any) =>val !== null && val !== undefined ? Number(val).toFixed(2) : (0).toFixed(2);

      this.form = new FormGroup({
        max_total_applications: new FormControl(existingData?.max_total_applications ?? 0, Validators.required),
        max_total_bonus: new FormControl(toFixed(existingData?.max_total_bonus ?? 0), Validators.required),
        phase1_starting_amount: new FormControl(toFixed(phase1_starting_amount)),
        phase1_target_type: new FormControl(phase1_target_type),
        phase1_target_amount: new FormControl(toFixed(phase1_target_amount)),
        phase2_starting_amount: new FormControl(toFixed(phase2_starting_amount)),
        phase2_target_type: new FormControl(existingData?.phase2_target_type ?? 6, Validators.required),
        phase2_target_amount: new FormControl(toFixed(existingData?.phase2_target_amount ?? 0), Validators.required),
        max_transfer_out: new FormControl(toFixed(existingData?.max_transfer_out ?? 0), Validators.required),
        current_players: new FormControl(existingData?.current_players ?? 0),
        used_budget: new FormControl(existingData?.used_budget ?? 0),
        amount_per_line: new FormControl(existingData?.amount_per_line > 0 ? existingData.amount_per_line : 0.01),
        rounds: new FormControl(existingData?.rounds > 0 ? existingData.rounds : 1),
        lines: new FormControl(existingData?.lines > 0 ? existingData.lines : 1),
        coins: new FormControl(existingData?.coins > 0 ? existingData.coins : 1),
      });


      if (this.initialData.promotion_type == 'PP Promotion') {
        this.form.patchValue({
          phase1_starting_amount: 0,
          phase1_target_type: 7, // winover
          amount_per_line: existingData?.amount_per_line > 0 ? existingData.amount_per_line : 0.01,
          rounds: existingData?.rounds > 0 ? existingData.rounds : 1,
        })
        this.form.get('phase1_starting_amount').clearValidators();
        this.form.get('phase1_target_type').clearValidators();
        this.form.get('phase1_target_amount')?.setValidators(Validators.required);
        this.form.get('amount_per_line')?.setValidators([Validators.required, Validators.min(0.01)]);
        this.form.get('rounds')?.setValidators([Validators.required, Validators.min(1)]);
        // Phase 2 Starting Amount is the credit the member starts Phase 2 with — 0 grants nothing,
        // and the API only validates it as `numeric`, so the floor has to be enforced here.
        this.form.get('phase2_starting_amount')?.setValidators([Validators.required, Validators.min(0.01)]);

        ['phase1_starting_amount', 'phase1_target_type', 'phase1_target_amount', 'amount_per_line', 'rounds', 'phase2_starting_amount'].forEach(field => {
          this.form.get(field)?.updateValueAndValidity();
        });
      }
      else if (this.initialData.promotion_type == 'Free Spin') {
        // Free Spin: only the free-round params apply — the phase-based fields are unused,
        // so clear their validators and require amount_per_line / rounds / lines / coins.
        this.form.patchValue({
          amount_per_line: existingData?.amount_per_line > 0 ? existingData.amount_per_line : 0.01,
          rounds: existingData?.rounds > 0 ? existingData.rounds : 1,
          lines: existingData?.lines > 0 ? existingData.lines : 1,
          coins: existingData?.coins > 0 ? existingData.coins : 1,
        });

        ['phase1_starting_amount', 'phase1_target_type', 'phase1_target_amount', 'phase2_starting_amount', 'phase2_target_type', 'phase2_target_amount'].forEach(field => {
          this.form.get(field)?.clearValidators();
          this.form.get(field)?.updateValueAndValidity();
        });

        this.form.get('amount_per_line')?.setValidators([Validators.required, Validators.min(0.01)]);
        this.form.get('rounds')?.setValidators([Validators.required, Validators.min(1)]);
        this.form.get('lines')?.setValidators([Validators.required, Validators.min(1)]);
        this.form.get('coins')?.setValidators([Validators.required, Validators.min(1)]);
        ['amount_per_line', 'rounds', 'lines', 'coins'].forEach(field => {
          this.form.get(field)?.updateValueAndValidity();
        });

        // Free Spin per-currency fields (AIO-71840) — Min Transfer + Status
        // Status defaults to inactive (0): currencies are opt-in, only ON currencies get submitted.
        this.form.addControl('min_transfer', new FormControl(toFixed(existingData?.min_transfer ?? 0), [Validators.required]));
        this.form.addControl('status', new FormControl(existingData?.status ?? 0));
      }
      else {
        this.form.patchValue({
          amount_per_line: 0,
          rounds: 0,
        })
      }

      this.currencyForms.set(currency.id, this.form);
    });
  }

  selectCurrency(currencyId: number): void {
    this.activeCurrencyId = currencyId;
    const found = this.dropdown.currencies.find(c => c.id === currencyId);
    this.activeCurrency = found ? found.name : '';
    // Bet-value providers: refresh the Amount Per Line bet values for the newly-selected currency
    if (this.hasBetValues && found) {
      this.loadBetValuesForCurrency(found);
    }
  }

  getCurrencyForm(currencyId: number): FormGroup {
    return this.currencyForms.get(currencyId) || new FormGroup({});
  }

  // PP Promotion: Phase 2 Starting Amount must be greater than zero. Surfaced inline under the field
  // so the operator can see why the step's red dot and the disabled Submit / Update are there.
  isPhase2StartingAmountInvalid(): boolean {
    const control = this.getCurrencyForm(this.activeCurrencyId).get('phase2_starting_amount');
    return !!control && control.invalid;
  }

  // A currency is "configured" when it was already saved on this promotion (it has a provider free-round grant).
  // On Free Spin edit a configured currency's free-round params stay readonly (the grant can't change), but a
  // newly-activated currency stays editable so the operator can set its params before updateV4 grants it.
  isCurrencyConfigured(currencyId: number): boolean {
    return this.currencyApiData.has(currencyId);
  }

  activeCurrencyStats(type: any) {
    let apiData = this.currencyApiData.get(this.activeCurrencyId);
    if (apiData) {
      switch(type) {
        case 'current_players':
          return apiData.current_players;
        case 'used_budget':
          return apiData.used_budget;
      }
    }
    return 0
  }

  private getAllFormData(): any[] {
    const result: any[] = [];
    this.currencyForms.forEach((form, currencyId) => {
      result.push({
        ...form.value,
        currency_id: currencyId,
      });
    });
    return result;
  }

  onNumberKeydown(event: KeyboardEvent, controlName?: any) {
    if (controlName === 'rounds') {
      // block dot
      if (controlName === 'rounds' && event.key === '.') {
        event.preventDefault();
      }
    }

    // block minus & scientific notation
    if (event.key === '-' || event.key === 'e') {
      event.preventDefault();
    }
  }
  
  checkAndReset(controlName: string) {
    const control = this.getCurrencyForm(this.activeCurrencyId).get(controlName);
    if (!control) return;

    const value = control.value;

    if (controlName == "amount_per_line" && (value === null || value === undefined || value === '' || /^\s*$/.test(value))) {
      control.setValue(0.01);
    }
    else if (controlName == "rounds") {
      if (value === null || value === undefined || value === '' || /^\s*$/.test(value) || value < 1) {
        control.setValue(1);
      }
      else if (value > 1000) {
         control.setValue(1000);
      }
    }
    else if (controlName != "amount_per_line" && controlName != "rounds" && (value === null || value === undefined || value === '' || /^\s*$/.test(value))) {
      control.setValue(0);
    }
  }

  onPaste(event: ClipboardEvent, controlName?: string) {
    const clipboardData = event.clipboardData;
    const pastedText = clipboardData?.getData('text');
    
    if (pastedText && (pastedText.includes('-') || pastedText.toLowerCase().includes('e'))) {
      event.preventDefault();
    }
    else {
      if (controlName == 'rounds' || controlName == 'amount_per_line') {
        this.getCurrencyForm(this.activeCurrencyId).patchValue({
          [controlName]: pastedText
        })
      }
    }
  }
}
